import { useTranslation } from "react-i18next";
import { Order } from "../../../../../types/order";
import { RootState } from "../../../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { Reservation } from "../../../../../types/bookingCreation";
import { atStation } from "../../../../../util/stationTime";
import { useSelectedLocationTimeZone } from "../../../../../util/locationTimeZone";
import { couponRedemptionLoading } from "../../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { useState } from "react";
import { formatCurrency, getEquipmentName } from "../../../../../util";
import { useApplyCoupon } from "../../../../../redux/flows/applyCouponFlow";
import { calculateDuration } from "../../../../../util/date";
import { cn } from "../../../../../util/cn";
import { i18n } from "../../../../../i18n/i18n";
import LoaderOverlay from "../../../../layout/components/LoaderOverlay";
import {
  Coupon,
  calculateDisplayPrice,
  consolidateCoupons,
  isFreeOfCharge,
} from "../../../../../util/payments";
import CustomButton from "../../../common/util/CustomButton";
import { bookedItemLabel } from "../../../common/bookedItemLabel";
import { useTaxStatement } from "../../../../../util/taxStatement";

interface BookingTicketProps {
  order?: Order;
  location?: string;
  reservations: Reservation[];
  amount?: number;
}

const BookingTicket = (props: BookingTicketProps) => {
  const { location, reservations, amount } = props;
  const [couponCode, setCouponCode] = useState<string>("");
  const { t } = useTranslation();
  const taxStatement = useTaxStatement();
  const dispatch = useDispatch();
  const tenantConfig = useSelector((state: RootState) => state.tenantConfig.config);
  // The booked location's clock (#3219).
  const timeZone = useSelectedLocationTimeZone();
  const selectedLocation = useSelector(
    (state: RootState) => state.bookingCreation.selectedLocation
  );
  const equipments = useSelector((state: RootState) =>
    selectedLocation ? state.bookingCreation.groupsByLocation[selectedLocation.id] : undefined
  );
  const recipientData = useSelector((state: RootState) => state.bookingCreation.recipientData);
  const isCouponReedemptionLoading = useSelector(
    (state: RootState) => state.bookingCreation.loading.couponRedemption
  );
  const [showLoading, setShowLoading] = useState(false);
  const findEquipmentName = (groupId: string) => {
    if (!equipments) return "";
    const targetGroup = equipments.find((group: any) => group.id === groupId);
    if (targetGroup) {
      return getEquipmentName(targetGroup.name, targetGroup?.langNames, "title");
    }

    return ""; // Return empty if no matching group is found
  };

  // Basic coupon validation - alphanumeric with optional dashes/underscores
  const isValidCoupon = (code: string) => {
    return /^[a-zA-Z0-9-_ ]+$/.test(code) && code.trim().length > 0;
  };
  const isApplyButtonDisabled = !isValidCoupon(couponCode);

  // Deliberately NOT util/payments' calculateTotalPrice, which sums every
  // reservationItem — this one takes item[0] only. Nor calculateRequestedAmount,
  // which prefers payment.requested_amount: this is the gross list price the
  // ticket prints, before any coupon. Swapping in either would move the number on
  // screen, so it stays local until someone can verify p2p against real data.
  const calculateTotalPrice = (reservations: Reservation[]): number => {
    return reservations.reduce((total, reservation) => {
      let price = reservation.reservationItems[0].price;
      if (typeof price === "string") {
        price = parseFloat(price);
      }

      if (isNaN(price)) {
        console.warn(`Invalid price detected: ${price}`);
        return total;
      }

      return total + price;
    }, 0);
  };

  // Deliberately NOT util/payments' findCoupons: that one reads
  // payment.discount_amount_applied, this one derives the discount as
  // price − requested_amount. The two can disagree, so repointing it would change
  // the discount shown here. Left local pending verification against real p2p data.
  const findCoupons = (reservations: Reservation[]) => {
    const foundCoupons: Coupon[] = [];

    for (const reservation of reservations) {
      const code = reservation.payment.voucher?.code;
      const amount = parseFloat(
        (reservation.reservationItems[0].price - reservation.payment.requested_amount!).toFixed(2)
      );

      if (code && amount) {
        foundCoupons.push({ code, amount });
      }
    }

    return foundCoupons;
  };

  const totalPrice = calculateTotalPrice(reservations);

  const applyCoupon = useApplyCoupon();
  const onApplyCoupon = async () => {
    setShowLoading(false);
    dispatch(couponRedemptionLoading({ loading: true }));

    const reservationIds = reservations.map((reservation: any) => {
      return reservation.reservationId;
    });

    await applyCoupon(reservationIds, couponCode);

    setCouponCode("");
  };

  const coupons = findCoupons(reservations);
  const displayPrice = calculateDisplayPrice(totalPrice, coupons);

  // A promo code cannot reduce a free booking. ADR-0008 amendment 2026-09-16.
  // Reads the LOCAL total above — the gross list price this ticket prints —
  // for the same reason that one stays local: the number on screen and the
  // rule about it must not come from two different calculations.
  const free = isFreeOfCharge(totalPrice, reservations.length);
  const showCouponButton = tenantConfig.allowCoupons && coupons.length === 0 && !free;

  const consolidatedCoupons = consolidateCoupons(coupons);
  const textBoldClass = "text-foreground/80 font-bold leading-5 tracking-[0.02em] break-words";

  // Ticket view after placing the Order ==> we retrieve the location
  //   const retrievedLocation = useSelector(Selector.Location.makeLocation(order?.locationId ?? ''));

  return (
    <div className="bg-background shadow-md flex flex-col py-8 px-5 border border-foreground/10 relative mx-5 rounded-md text-foreground">
      <div className="absolute -right-px top-[29%] h-[14px] w-[7px] rounded-l-full border border-r-0 border-foreground/10 bg-white" />
      <div className="absolute -left-px top-[29%] h-[14px] w-[7px] rounded-r-full border border-l-0 border-foreground/10 bg-white" />
      <div className="flex flex-col gap-2">
        <div>
          <p>{t("orders.ticket_overview.location")}</p>
          <p className={textBoldClass}>{location}</p>
        </div>
        <div>
          <p>{t("orders.ticket_overview.recipient_details")}</p>
          <p className={textBoldClass}>
            {recipientData.firstName} {recipientData.lastName}
          </p>
          <p className={textBoldClass}>{recipientData.email}</p>
          {recipientData && recipientData.phone !== "+" && (
            <p className={textBoldClass}>{recipientData.phone}</p>
          )}
        </div>
        <div>
          <p>{t("orders.ticket_overview.booked_items")}</p>
          {reservations.map((reservation, _) => {
            // Extract start and end dates from reservationItems
            const reservationStartDate = atStation(reservation.reservationItems[0].fromDate, timeZone);
            const reservationEndDate = atStation(reservation.reservationItems[0].toDate, timeZone);
            const equipmentName = findEquipmentName(reservation.reservationItems[0].groupId);
            const price = reservation.reservationItems[0].price;
            const duration = calculateDuration(
              reservationStartDate.toDate(),
              reservationEndDate.toDate()
            );
            return (
              <div
                key={reservation.id}
                className="w-full flex flex-col mb-1.5 border-b border-foreground/45"
              >
                <div
                  className={cn(
                    textBoldClass,
                    // Same wrapping rules as the storage summary: the
                    // description gets the slack and may wrap, the price never
                    // does. See `TemporaryStorageBookingSummaryStep`.
                    "flex w-full flex-row items-start justify-between gap-3"
                  )}
                >
                  <strong className="min-w-0 flex-1">
                    {bookedItemLabel(
                      amount,
                      equipmentName,
                      t("orders.ticket_overview.confirmation_step.locker")
                    )}{" "}
                    -{" "}
                    {duration.days > 0 &&
                      `${duration.days} ${
                        duration.days === 1
                          ? t("general.duration_day_singular")
                          : t("general.duration_day_plural")
                      }`}
                    {duration.hours > 0 && ` ${duration.hours}${t("general.duration_hour_abbr")}`}{" "}
                    {duration.minutes > 0 &&
                      ` ${duration.minutes}${t("general.duration_minute_abbr")}`}{" "}
                  </strong>
                  <strong className="shrink-0 whitespace-nowrap">
                    {formatCurrency(price, i18n.language)}
                  </strong>
                </div>
                <div>
                  <p>
                    {reservationStartDate.format("L")}{" "}
                    {reservationStartDate.format("LT")}
                  </p>
                </div>
                <div className="flex flex-row justify-between">
                  <p>
                    {reservationEndDate.format("L")}{" "}
                    {reservationEndDate.format("LT")}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="">
        {consolidatedCoupons.map((coupon) => {
          return (
            <div className="flex flex-row justify-between" key={coupon.code}>
              <p>
                {t("book_location.confirmation.coupon_item")} ({coupon.code})
              </p>
              <p className={textBoldClass}>
                {!showLoading
                  ? formatCurrency(
                      (coupon.amount > totalPrice ? totalPrice : coupon.amount) * -1,
                      i18n.language
                    )
                  : "..."}
              </p>
            </div>
          );
        })}

        <div className="flex flex-row justify-between">
          <p>{t("book_location.confirmation.total_items")}</p>
          <p className={textBoldClass}>
            {!showLoading ? formatCurrency(displayPrice, i18n.language) : "..."}
          </p>
        </div>
        {showCouponButton && (
          <div className="flex flex-row justify-between items-center gap-4 my-2">
            <input
              type="text"
              aria-label={t("general.enter_coupon_code")}
              placeholder={t("general.enter_coupon_code")}
              onChange={(e) => setCouponCode(e.target.value)}
              className=" px-3 h-10 py-2 w-full border border-foreground/10 bg-background rounded-lg outline-none focus:border-primary"
            />
            <CustomButton
              onClick={onApplyCoupon}
              loading={isCouponReedemptionLoading}
              disabled={isApplyButtonDisabled}
              spinnerColorClass="border-onPrimary border-t-primary"
              className="mt-2 bg-primary text-onPrimary"
            >
              {t("orders.ticket_overview.button")}
            </CustomButton>
          </div>
        )}
        {/* No price, nothing for a tax statement to be about. */}
        {!free && (
          <p className="text-sm">{taxStatement.sentence}</p>
        )}
      </div>
      <LoaderOverlay open={showLoading} />
    </div>
  );
};
export default BookingTicket;
